import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAccount } from 'wagmi'
import { parseUnits } from 'viem'
import { T } from '@/lib/theme'
import { AmountSlider, Button, MaxLink, Panel, SectionLabel, ToggleButton } from '@/components/ui'
import { TokenGlyph } from '@/components/dashboard/TokenGlyph'
import { SimulationRows } from '@/components/dashboard/detail/SimulationRows'
import { ADDRESSES } from '@/lib/contracts'
import stakingVaultAbi from '@/lib/abis/StakingVault.json'
import farmAbi from '@/lib/abis/HorizonEmissions.json'
import { useStakingVaults } from '@/hooks/useStakingVaults'
import { useAction } from '@/hooks/useAction'
import { headerRow, headCell, row } from '@/components/dashboard/tableChrome'
import { fmt, fmtBalance, fmtBalanceWei, fmtUsd, validateAmount, weiToInputString } from '@/lib/utils'

// ─────────────────────────────────────────────────────────────────
// Single-asset staking: one token in, HORIZON out, no pairing and no impermanent loss.
//
// Sits on the Vaults page beneath the ALM vaults because it is the same promise from the user's
// side - deposit, earn - while being a genuinely different instrument. A ALM vault earns trading
// fees on a two-token position and carries divergence risk; this earns emissions on a balance that
// never changes size. Presenting them as one list would flatten exactly the distinction someone
// choosing between them needs.
//
// Every row is read from StakingVaultFactory's registry, so listing an asset is a genesis
// transaction with no frontend deploy - see useStakingVaults.
// ─────────────────────────────────────────────────────────────────

export function StakingVaultsPanel() {
  const { vaults, refetch, notDeployed } = useStakingVaults()
  const { address: user } = useAccount()
  const [openRow, setOpenRow] = useState(null)

  // A retired vault stays withdrawable, so it stays visible while anyone is still in it - dropping
  // the row would strand a position with no way to reach it from the app.
  const visible = vaults.filter((v) => !v.retired || v.user.staked > 0)
  if (notDeployed || visible.length === 0) return null

  return (
    <div style={{ marginTop: 40 }}>
      <SectionLabel>Single-asset staking</SectionLabel>
      <div style={intro}>
        Stake one token and earn HORIZON. Your deposit stays whole while HORIZON rewards accumulate
        on top.
      </div>

      <Panel style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 820 }}>
            <div style={headerRow}>
              <span style={{ ...headCell, flex: '1 1 auto' }}>Asset</span>
              <span style={{ ...headCell, width: 150, textAlign: 'right' }}>HORIZON / day</span>
              <span style={{ ...headCell, width: 140, textAlign: 'right' }}>Total staked</span>
              <span style={{ ...headCell, width: 140, textAlign: 'right' }}>Your stake</span>
              <span style={{ ...headCell, width: 90, textAlign: 'right' }}>Earned</span>
            </div>
            {visible.map((v) => (
              <StakingVaultRow
                key={v.address}
                vault={v}
                user={user}
                onTx={refetch}
                open={openRow === v.address}
                onToggle={() => setOpenRow(openRow === v.address ? null : v.address)}
              />
            ))}
          </div>
        </div>
      </Panel>
    </div>
  )
}

/// One row, expanding in place to the stake/unstake form.
///
/// The two CLM tables above navigate to `/vaults/:address` on click. These deliberately do not:
/// a staking vault has no detail route, and inventing one to hold a two-field form would be a page
/// with nothing else on it. Expanding in place keeps the table shape the request asked for while
/// leaving the form exactly one click away — closer than the ALM vaults manage, in fact.
function StakingVaultRow({ vault, user, onTx, open, onToggle }) {
  return (
    <div>
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle() } }}
        style={{ ...row, cursor: 'pointer', transition: 'background 0.15s ease' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)' }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
      >
        <div style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <TokenGlyph symbol={vault.symbol} size={40} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: T.text, fontFamily: T.fontD, fontSize: T.fs.ui, fontWeight: 700 }}>
                {vault.symbol}
              </span>
              {vault.retired && <span style={pill(T.textDim)}>Retired</span>}
              {!vault.retired && vault.depositsPaused && <span style={pill(T.warning)}>Paused</span>}
            </div>
            <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
              Single-asset · {termLabel(vault)}
            </div>
          </div>
        </div>
        <span className="mono" style={{ ...cell, width: 150, color: T.positive }}>
          {vault.horizonPerDay == null ? '—' : fmtBalance(vault.horizonPerDay, 2)}
        </span>
        <span className="mono" style={{ ...cell, width: 140 }}>{fmt(vault.totalStaked, 2)}</span>
        <span className="mono" style={{ ...cell, width: 140 }}>
          {user ? fmt(vault.user.staked, 4) : '—'}
        </span>
        <span className="mono" style={{
          ...cell, width: 90, color: vault.user.pendingHorizon > 0 ? T.positive : T.textDim,
        }}>
          {user ? fmtBalance(vault.user.pendingHorizon, 2) : '—'}
        </span>
      </div>
      {open && (
        <div style={{ padding: '0 20px 20px', borderBottom: `1px solid ${T.border}` }}>
          <StakingVaultCard vault={vault} user={user} onTx={onTx} />
        </div>
      )}
    </div>
  )
}

function StakingVaultCard({ vault, user, onTx }) {
  const action = useAction()
  const [mode, setMode] = useState('stake')
  const [amount, setAmount] = useState('')
  const isStake = mode === 'stake'

  const wei = useMemo(() => {
    try { return amount && !validateAmount(amount) ? parseUnits(amount, vault.decimals) : 0n } catch { return 0n }
  }, [amount, vault.decimals])

  const maxWei = isStake ? vault.user.walletWei : vault.user.stakedWei
  const closed = vault.depositsPaused || vault.retired

  // Share of the farm this deposit would produce, and what that share pays per day. Stated in
  // HORIZON rather than as a percentage on purpose: the staked asset has no oracle here (nothing is
  // borrowed against it, so none is needed), and an APR would need a price this app does not have.
  const typed = Number(wei) / 10 ** vault.decimals
  const nextStaked = isStake ? vault.user.staked + typed : Math.max(0, vault.user.staked - typed)
  const nextTotal = isStake ? vault.totalStaked + typed : Math.max(0, vault.totalStaked - typed)
  const dailyNow = vault.horizonPerDay != null && vault.totalStaked > 0
    ? vault.horizonPerDay * (vault.user.staked / vault.totalStaked)
    : 0
  const dailyNext = vault.horizonPerDay != null && nextTotal > 0
    ? vault.horizonPerDay * (nextStaked / nextTotal)
    : 0
  const active = wei > 0n

  return (
    // No panel wrapper and no asset header: this renders INSIDE a table row that already carries
    // the glyph, the symbol, the status pill, the per-day rate, the totals and the term. Repeating
    // any of it here would make the expanded row read as a second, disagreeing summary of the row
    // above it. What is left is the part the table cannot show — the blurb, the claimable balance,
    // and the form itself.
    <div>
      {vault.blurb && <div style={blurbStyle}>{vault.blurb}</div>}

      {vault.horizonPerDayUsd != null && vault.horizonPerDay > 0 && (
        <div style={rateSub}>≈ {fmtUsd(vault.horizonPerDayUsd)} per day, shared across all stakers</div>
      )}
      {vault.horizonPerDay === 0 && (
        <div style={rateSub}>This market is listed and awaiting its emission budget.</div>
      )}

      {vault.user.pendingHorizon > 0 && (
        <div style={pendingRow}>
          <span style={{ color: T.positive, fontFamily: T.fontM, fontSize: T.fs.caption }}>
            {fmtBalance(vault.user.pendingHorizon, 6)} HORIZON earned
            {vault.user.pendingHorizonUsd != null ? ` · ≈ ${fmtUsd(vault.user.pendingHorizonUsd)}` : ''}
          </span>
          <Button variant="secondary" style={claimBtn} disabled={action.pending} onClick={claim}>
            {action.pending ? 'Working…' : 'Claim'}
          </Button>
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', gap: 6 }}>
        <ToggleButton active={isStake} onClick={() => { setMode('stake'); setAmount('') }} disabled={closed}>Stake</ToggleButton>
        <ToggleButton active={!isStake} onClick={() => { setMode('unstake'); setAmount('') }}>Unstake</ToggleButton>
      </div>

      <input
        type="text" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
        style={{ ...inputBox, marginTop: 12 }}
      />
      <div style={{ textAlign: 'right', marginTop: 4 }}>
        <span style={{ color: T.textDim, fontSize: T.fs.caption, fontFamily: T.fontM }}>
          {isStake ? 'Wallet' : 'Staked'}: {user ? fmtBalanceWei(maxWei, vault.decimals) : '-'} {vault.symbol}
          {user && <MaxLink onClick={() => setAmount(weiToInputString(maxWei, vault.decimals))} />}
        </span>
      </div>
      <AmountSlider
        valueWei={wei} maxWei={maxWei}
        onChangeWei={(w) => setAmount(weiToInputString(w, vault.decimals))} disabled={!user}
      />

      <SimulationRows rows={[
        {
          label: 'Your stake',
          before: vault.user.staked,
          after: active ? nextStaked : undefined,
          format: (v) => `${fmt(v, 4)} ${vault.symbol}`,
          good: 'neutral',
        },
        {
          label: 'Your HORIZON per day',
          help: 'Your share of this vault’s emission, at the current total staked. It falls as others stake alongside you.',
          before: dailyNow,
          after: active ? dailyNext : undefined,
          format: (v) => fmtBalance(v, 4),
          good: 'up',
        },
      ]} />

      <div style={{ marginTop: 14 }}>
        {!user ? (
          <Button disabled style={btnFull}>Connect wallet</Button>
        ) : isStake && closed ? (
          <Button disabled style={btnFull}>{vault.retired ? 'Retired' : 'Deposits paused'}</Button>
        ) : (
          <Button onClick={submit} disabled={!wei || action.pending} style={btnFull}>
            {action.pending ? 'Working…' : isStake ? 'Stake' : 'Unstake'}
          </Button>
        )}
      </div>

      {action.error && (
        <div style={{ color: T.negative, marginTop: 10, fontSize: T.fs.caption }}>
          {String(action.error)}
        </div>
      )}
    </div>
  )

  async function submit() {
    // Staking pulls the asset into the vault, so it carries that approval; unstaking pulls nothing.
    const intent = isStake
      ? {
          pulls: [{ token: vault.asset, spender: vault.address, amount: wei }],
          calls: [{ address: vault.address, abi: stakingVaultAbi, functionName: 'deposit', args: [wei] }],
          permit: { token: vault.asset, amount: wei, spender: vault.address,
            buildCall: (p, sig) => ({ address: vault.address, abi: stakingVaultAbi, functionName: 'depositWithPermit', args: [p, sig] }) },
          successMsg: `Staked ${amount} ${vault.symbol}.`,
        }
      : { calls: [{ address: vault.address, abi: stakingVaultAbi, functionName: 'withdraw', args: [wei] }], successMsg: `Unstaked ${amount} ${vault.symbol}.` }
    const res = await action.run(intent)
    if (!res) return
    setAmount('')
    onTx?.()
  }

  async function claim() {
    // Claims go through the emissions accountant, not the vault - the vault holds no HORIZON and
    // never has. Same call the ALM vaults use, because it is the same farm table.
    const res = await action.run({ calls: [{ address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'claimVaultIncentive', args: [vault.address] }], successMsg: 'HORIZON claimed.' })
    if (res) onTx?.()
  }
}

/// What the farm's term means, in words rather than a raw timestamp.
///
/// Rewards that have already accrued stay claimable after a term ends — the stream stops, the
/// balance does not vanish — so the copy says "stopped", never anything that reads as forfeiture.
function termLabel(vault) {
  if (vault.endsAt === 0) {
    return vault.horizonPerDay > 0 ? 'Rewards run until further notice.' : 'Rewards begin once a budget is set.'
  }
  const endsMs = vault.endsAt * 1000
  const when = new Date(endsMs).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  if (Date.now() >= endsMs) {
    return `Rewards ran until ${when}. Anything you already earned stays claimable.`
  }
  const days = Math.max(1, Math.ceil((endsMs - Date.now()) / 86_400_000))
  return `Rewards run until ${when}, about ${days} day${days === 1 ? '' : 's'} left.`
}

const intro = {
  marginTop: 8, marginBottom: 20, color: T.textMuted, fontFamily: T.fontM,
  fontSize: T.fs.body, lineHeight: 1.6, maxWidth: 720,
}
const blurbStyle = {
  marginTop: 14, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6,
}
const rateRow = {
  marginTop: 16, padding: '14px 16px', borderRadius: T.r.md,
  border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.02)',
}
const rateLabel = {
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700,
  letterSpacing: '0.04em', textTransform: 'uppercase',
}
const rateValue = {
  marginTop: 4, color: T.positive, fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 700,
}
const cell = {
  color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body,
  textAlign: 'right', flexShrink: 0,
}
const rateSub = { marginTop: 3, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }
const pendingRow = {
  marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 12, flexWrap: 'wrap',
}
const claimBtn = { padding: '6px 14px', fontSize: T.fs.caption }
const btnFull = { width: '100%', padding: '12px 16px', fontSize: T.fs.body }
const inputBox = {
  width: '100%', background: T.bg2, border: `1px solid ${T.border}`, borderRadius: T.r.md,
  padding: '10px 12px', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.25)',
  color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, outline: 'none', boxSizing: 'border-box',
}
function pill(color) {
  return {
    marginLeft: 'auto', padding: '3px 10px', borderRadius: T.r.pill,
    border: `1px solid ${color}66`, background: `${color}18`, color,
    fontFamily: T.fontM, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
  }
}
